import { Badge } from "./Badge.js";
import type { StockAvailability, StockInfo } from "../lib/types.js";

/**
 * StockBadge — vizualizace dostupnosti dealer nebo výrobce skladu.
 * Mapování:
 *   available (>0 ks)       → zelená
 *   manufacturerAvailable   → modrá (pouze u výrobce)
 *   onOrder                 → oranžová
 *   notAvailable (0 ks)     → červená
 *   unknown                 → neutrální
 */

function availabilityTone(
  a: StockAvailability,
  qty?: number | null,
): "green" | "amber" | "red" | "blue" | "neutral" {
  if (a === "available" && (qty ?? 0) > 0) return "green";
  if (a === "manufacturerAvailable") return "blue";
  if (a === "onOrder") return "amber";
  if (a === "notAvailable") return "red";
  return "neutral";
}

function availabilityLabel(a: StockAvailability, qty?: number | null): string {
  if (a === "available") {
    if ((qty ?? 0) > 0) return `${qty} ks skladem`;
    return "skladem";
  }
  if (a === "manufacturerAvailable") return "k dispozici u výrobce";
  if (a === "onOrder") return "na objednávku";
  if (a === "notAvailable") return "není skladem";
  return "neznámo";
}

export function StockBadge({
  label,
  stock,
  emphasis = "sm",
}: {
  label: string;
  stock: StockInfo;
  emphasis?: "sm" | "md";
}): JSX.Element {
  const tone = availabilityTone(stock.availability, stock.qty);
  const size = emphasis === "md" ? "text-sm" : "text-xs";
  return (
    <div className={`flex flex-col gap-0.5 ${size}`}>
      <span className="text-zinc-500 text-[11px] uppercase tracking-wide">
        {label}
      </span>
      <Badge tone={tone} className="self-start">
        {availabilityLabel(stock.availability, stock.qty)}
      </Badge>
    </div>
  );
}
